/**
 * Helper Class for creating tables, with input fields etc.
 */
import TableElements from './TableElements';
import TableEvents from './TableEvents';
import DataParser from '../../helpers/DataParser';

export default class Table {
    /**
     * @property {Object} TableElements helper class
     * @property {Object} TableEvents helper class
     * @returns void
     */
    constructor() {
        this.tableElements = new TableElements();
        this.tableEvents = new TableEvents();
        this.dataParser = new DataParser();
    }

    /**
     * @description Clear table from html structure and data
     * @param {Object} parent parent element
     * @returns void
     */
    removeAllChildNodes(parent) {
        while (parent.firstChild) {
            parent.removeChild(parent.firstChild);
        }
    }

    /**
     * @description Add and create Table header
     * if Property keys arguments exists, filter out unwanted data
     * @param {string} tableId static table id
     * @param {Object} data data from back end
     * @param {Object} tableProps json object with properties/settings
     * @returns void
     */
    addTableHead(tableId, data, tableProps) {
        document.querySelector(`${tableId}`).setAttribute('status', 'false');
        this.removeAllChildNodes(document.querySelector(tableId));

        const headerFilter =
            typeof tableProps !== undefined
                ? this.dataParser.filterDataByKeys(data, tableProps, 'mappedKeys')
                : data;
        const headerData = typeof tableProps !== undefined ? headerFilter[0] : data;
        const tableRow = document.querySelector(tableId);
        const tableHeader = document.createElement('thead');

        let tableIndex = { rowNumber: 0, columnIndex: 0 };

        for (let key of headerData) {
            let tableHeaderColumn = document.createElement('th');
            let tableHeaderText = document.createTextNode(this.convertCamelCase(key[0]));

            if (typeof tableProps !== undefined) {
                for (let active of Object.values(tableProps.activeKeys)) {
                    if (active.key === key[0]) {
                        tableHeaderColumn.setAttribute(
                            'class',
                            `background-col-${tableIndex.columnIndex}`
                        );
                    }
                }
            }

            tableHeaderColumn.appendChild(tableHeaderText);
            tableHeader.appendChild(tableHeaderColumn);
            tableIndex.columnIndex++;
        }

        tableRow.appendChild(tableHeader);
    }

    /**
     * @description Create table with rows and inputs
     * @param {string} tableId static table id
     * @param {Object} data data from back end
     * @param {string} renderType Used when updating a row
     * @returns void
     */
    addTableBody(tableId, data, tableProps) {
        let tableIndex = { rowIndex: 0, columnIndex: 0 };
        let dataId = '';
        const tableElement = document.querySelector(tableId);
        const bodyFilter =
            typeof tableProps !== undefined
                ? this.dataParser.filterDataByKeys(data, tableProps, 'mappedKeys')
                : data;
        const bodyData = typeof tableProps !== undefined ? bodyFilter : data;

        for (let element in bodyData) {
            let row = tableElement.insertRow();
            row.setAttribute('id', `row_${tableIndex.rowIndex}`);

            for (let key in bodyData[element]) {
                if (
                    typeof tableProps !== undefined &&
                    tableIndex.columnIndex === tableProps.mappedKeys.length
                ) {
                    tableIndex.columnIndex = 0;
                }

                let dataKey = bodyData[element][key][0];
                let dataValue = bodyData[element][key][1];
                let cell = row.insertCell();

                cell.setAttribute('id', `cell_${tableIndex.rowIndex}_${tableIndex.columnIndex}`);

                let inputType, inputWidth, inputStep, inputStepValue, inputNullValue;
                if (typeof tableProps !== undefined) {
                    for (let active of Object.values(tableProps.activeKeys)) {
                        if (active.key === dataKey) {
                            inputType = active.type;
                            inputWidth = active.width;
                            inputStep = active.step;
                            inputStepValue = active.stepValue;
                            inputNullValue = active.nullValue;
                        }
                    }
                }

                if (dataKey === 'Id') {
                    dataId = dataValue;
                    cell.parentNode.setAttribute('data-id', dataId);
                }

                cell.setAttribute('update-id', `${dataKey}_${dataId}`);

                let initValue = '';
                if (typeof tableProps !== undefined) {
                    initValue =
                        dataValue === null || dataValue === 'null' ? inputNullValue : dataValue;
                }

                this.tableElements.appendElements({
                    cell: cell,
                    dataId: dataId,
                    tableIndex: tableIndex,
                    data: dataValue,
                    settings: tableProps,
                    inputType: inputType,
                    inputWidth: inputWidth,
                    inputStep: inputStep,
                    inputStepValue: inputStepValue,
                    initValue: initValue,
                    icons: ['iEdit', 'iSave', 'iUndo'],
                });

                if (typeof tableProps !== undefined) {
                    for (let active of Object.values(tableProps.activeKeys)) {
                        if (active.key === dataKey) {
                            cell.setAttribute(
                                'class',
                                `background-col-${tableIndex.columnIndex} editable`
                            );
                            cell.querySelector('i').classList.remove('hidden');
                        }
                    }
                }
                tableIndex.columnIndex++;
            }
            tableIndex.rowIndex++;

            document.querySelector(`${tableId}`).setAttribute('status', 'true');
        }

        // Small cosmetic fix for position of save all button,
        // when rendering tables with alot of data.
        if (document.querySelector('.saveAll').length > 0) {
            if (tableIndex.rowIndex > 6) {
                document.querySelector('.saveAll').setAttribute('style', 'right: 36px !important;');
            } else {
                document.querySelector('.saveAll').removeAttribute('style');
            }
        }

        this.tableEvents.editableInputListener();
    }

    /**
     * @description Updating table data by id's after save
     * @param {Object} data data from back end
     * @param {Object} tableProps json object with properties/settings
     * @returns void
     */
    updateTableCells(data, tableProps) {
        const bodyFilter =
            typeof tableProps !== undefined
                ? this.dataParser.filterDataByKeys(data, tableProps, 'mappedKeys')
                : data;
        const bodyData = typeof tableProps !== undefined ? bodyFilter : data;

        for (let element in bodyData) {
            let dataId = '';
            for (let key in bodyData[element]) {
                let dataKey = bodyData[element][key][0];
                let dataValue = bodyData[element][key][1];

                if (dataKey === 'Id') {
                    dataId = dataValue;
                } else {
                    document.querySelector(`[update-id="${dataKey}_${dataId}"] span`).innerText =
                        dataValue;

                    document
                        .querySelector(`[update-id="${dataKey}_${dataId}"] input`)
                        .setAttribute('value', dataValue);
                }
            }
        }
    }

    /**
     * @description Convert string of camelCase text to two words.
     * @param {string} text
     * @returns {string} result converted string
     */
    convertCamelCase(text) {
        const regReplaceText = text.replace(/([A-Z])/g, ' $1');
        const result = regReplaceText.charAt(0).toUpperCase() + regReplaceText.slice(1);
        return result;
    }
}
